import { globexOpen, etWallToUtc } from '../nq-scalper/signals';
import { addDays, closureDates, isTradingDay, iso, parseIso } from '../../utils/marketCalendar';
import { isRth } from './shareMinutes';

export type BarFeed = 'spot' | 'cme';

export interface ChartInterval {
  unit: 'minute' | 'hour' | 'day' | 'week' | 'month';
  count: number;
}

/** UTC milliseconds. OpenAlgo receives seconds at the widget boundary. */
export interface CandleBar {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface QfBarRow {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number | null;
}

export type SessionFilter = 'rth' | 'all';

/** Indicator history, independent of the interval on screen. */
export const INDICATOR_SESSIONS = 15;

const MINUTE_LOOKBACK: Record<number, number> = {
  1: 2,
  5: 5,
  15: 15,
  30: 30,
  60: 60,
};

interface Clock {
  year: number;
  month: number;
  day: number;
  minutes: number;
}

export function sourceFor(symbol: string): BarFeed {
  const root = symbol.trim().toUpperCase().replace(/\.CME$/, '');
  return root === 'NQ' || root === 'ES' ? 'cme' : 'spot';
}

export function hasVolume(bars: readonly { volume: number }[]): boolean {
  return bars.some((bar) => bar.volume > 0);
}

export function lookbackSessionsFor(interval: ChartInterval): number {
  if (interval.unit === 'minute') {
    return MINUTE_LOOKBACK[interval.count] ?? Math.max(2, interval.count);
  }
  if (interval.unit === 'hour') return 20;
  if (interval.unit === 'day') return 252 * interval.count;
  return 400;
}

export function intervalMinutes(interval: ChartInterval): number {
  if (interval.unit === 'hour') return interval.count * 60;
  if (interval.unit === 'minute') return Math.max(1, interval.count);
  return 1;
}

export function fromQfBars(rows: readonly QfBarRow[]): CandleBar[] {
  return rows.map((row) => ({
    time: row.time * 1000,
    open: row.open,
    high: row.high,
    low: row.low,
    close: row.close,
    volume: row.volume ?? 0,
  }));
}

export function recentSessionDates(count: number, todayIso: string): string[] {
  const year = Number(todayIso.slice(0, 4));
  const closed = new Set<string>();
  [year - 1, year, year + 1].forEach((candidate) => {
    closureDates(candidate).forEach((day) => closed.add(day));
  });
  const out: string[] = [];
  let day = parseIso(todayIso);
  const guardLimit = count * 3 + 40;
  for (let guard = 0; out.length < count && guard < guardLimit; guard += 1) {
    if (isTradingDay(day, closed)) out.push(iso(day));
    day = addDays(day, -1);
  }
  return out.reverse();
}

function etClock(ms: number): Clock {
  const bag: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms))) {
    if (part.type !== 'literal') bag[part.type] = part.value;
  }
  let hour = Number(bag.hour);
  if (hour === 24) hour = 0;
  return {
    year: Number(bag.year),
    month: Number(bag.month),
    day: Number(bag.day),
    minutes: hour * 60 + Number(bag.minute),
  };
}

function shiftDay(year: number, month: number, day: number, delta: number): Clock {
  const utc = new Date(Date.UTC(year, month - 1, day + delta));
  return {
    year: utc.getUTCFullYear(),
    month: utc.getUTCMonth() + 1,
    day: utc.getUTCDate(),
    minutes: 0,
  };
}

function mondayKey(year: number, month: number, day: number): string {
  const utc = Date.UTC(year, month - 1, day);
  const dow = new Date(utc).getUTCDay();
  const delta = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(utc + delta * 86_400_000);
  return monday.toISOString().slice(0, 10);
}

/**
 * Session-anchored bucket start. The slot uses floor so a premarket minute
 * stays in the bucket that begins at or before it.
 */
export function sessionBucket(ms: number, widthMinutes: number): number {
  const width = Math.max(1, widthMinutes);
  const clock = etClock(ms);
  const open = 9 * 60 + 30;
  const slot = Math.floor((clock.minutes - open) / width);
  let minuteOfDay = open + slot * width;
  let { year, month, day } = clock;
  while (minuteOfDay < 0) {
    minuteOfDay += 24 * 60;
    ({ year, month, day } = shiftDay(year, month, day, -1));
  }
  while (minuteOfDay >= 24 * 60) {
    minuteOfDay -= 24 * 60;
    ({ year, month, day } = shiftDay(year, month, day, 1));
  }
  return etWallToUtc(year, month, day, Math.floor(minuteOfDay / 60), minuteOfDay % 60);
}

function aggregateBy(bars: readonly CandleBar[], keyOf: (bar: CandleBar) => number): CandleBar[] {
  const ordered = [...bars].sort((a, b) => a.time - b.time);
  const groups = new Map<number, CandleBar>();
  const order: number[] = [];
  for (const bar of ordered) {
    const key = keyOf(bar);
    const prev = groups.get(key);
    if (!prev) {
      groups.set(key, { ...bar, time: key });
      order.push(key);
      continue;
    }
    prev.high = Math.max(prev.high, bar.high);
    prev.low = Math.min(prev.low, bar.low);
    prev.close = bar.close;
    prev.volume += bar.volume;
  }
  return order.flatMap((key) => {
    const bar = groups.get(key);
    return bar ? [bar] : [];
  });
}

export function bucketMinutes(
  minutes: readonly CandleBar[],
  widthMinutes: number,
  session: SessionFilter,
): CandleBar[] {
  const kept = minutes.filter((bar) => (
    session === 'rth' ? isRth(bar.time) : globexOpen(bar.time)
  ));
  return aggregateBy(kept, (bar) => sessionBucket(bar.time, widthMinutes));
}

export function dailyFromMinutes(minutes: readonly CandleBar[]): CandleBar[] {
  const cash = minutes.filter((bar) => isRth(bar.time));
  return aggregateBy(cash, (bar) => {
    const clock = etClock(bar.time);
    return etWallToUtc(clock.year, clock.month, clock.day, 9, 30);
  });
}

/** Fold daily bars into one bar per ET week (Monday) or calendar month. */
export function aggregateEodBars(bars: readonly CandleBar[], unit: 'week' | 'month'): CandleBar[] {
  const groups = new Map<string, CandleBar>();
  const order: string[] = [];
  for (const bar of bars) {
    const { year, month, day } = etClock(bar.time);
    const key = unit === 'month' ? `${year}-${month}` : mondayKey(year, month, day);
    const prev = groups.get(key);
    if (!prev) {
      groups.set(key, { ...bar });
      order.push(key);
      continue;
    }
    prev.high = Math.max(prev.high, bar.high);
    prev.low = Math.min(prev.low, bar.low);
    prev.close = bar.close;
    prev.time = bar.time;
    prev.volume += bar.volume;
  }
  return order.flatMap((key) => {
    const bar = groups.get(key);
    return bar ? [bar] : [];
  });
}

function eodKey(ms: number, unit: 'week' | 'month'): string {
  const { year, month, day } = etClock(ms);
  return unit === 'month' ? `${year}-${month}` : mondayKey(year, month, day);
}

function withLivePrice(bar: CandleBar, print: CandleBar): CandleBar {
  return {
    ...bar,
    high: Math.max(bar.high, print.high),
    low: Math.min(bar.low, print.low),
    close: print.close,
  };
}

function inSession(time: number, interval: ChartInterval, session: SessionFilter): boolean {
  const intraday = interval.unit === 'minute' || interval.unit === 'hour';
  if (intraday) return session === 'rth' ? isRth(time) : globexOpen(time);
  return isRth(time);
}

function bucketMinutesOf(interval: ChartInterval): number {
  const intraday = interval.unit === 'minute' || interval.unit === 'hour';
  return intraday ? intervalMinutes(interval) : 24 * 60;
}

/** Index of the first bar at or after `time`. `bars` is sorted by time. */
function findTime(bars: readonly CandleBar[], time: number): number {
  let lo = 0;
  let hi = bars.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (bars[mid].time < time) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

function putBar(bars: readonly CandleBar[], index: number, bar: CandleBar): CandleBar[] {
  const next = bars.slice();
  if (index < next.length && next[index].time === bar.time) next[index] = bar;
  else next.splice(index, 0, bar);
  return next;
}

/**
 * Fold one live minute onto chart bars.
 *
 * A print for a bucket already on the chart updates that bar, including one
 * that is older than the tail. A missing bucket is inserted in time order.
 * A print with no volume leaves an existing bar's volume alone. Premarket
 * and the 17:00 break stay off an RTH series.
 */
export function mergeLiveMinute(
  bars: readonly CandleBar[],
  print: CandleBar,
  interval: ChartInterval,
  session: SessionFilter,
): readonly CandleBar[] {
  if (!Number.isFinite(print.time)) return bars;
  const unit = interval.unit;
  if (unit === 'week' || unit === 'month') {
    if (!isRth(print.time)) return bars;
    const key = eodKey(print.time, unit);
    const index = bars.findIndex((bar) => eodKey(bar.time, unit) === key);
    if (index >= 0) {
      const next = bars.slice();
      next[index] = withLivePrice(bars[index], print);
      return next;
    }
    const stamped = { ...print, time: sessionBucket(print.time, 24 * 60) };
    return putBar(bars, findTime(bars, stamped.time), stamped);
  }
  if (!inSession(print.time, interval, session)) return bars;
  const bucket = sessionBucket(print.time, bucketMinutesOf(interval));
  const index = findTime(bars, bucket);
  if (index < bars.length && bars[index].time === bucket) {
    const next = bars.slice();
    next[index] = withLivePrice(bars[index], print);
    return next;
  }
  return putBar(bars, index, {
    time: bucket,
    open: print.open,
    high: print.high,
    low: print.low,
    close: print.close,
    volume: print.volume,
  });
}

/**
 * True when `print` lands more than one bar past the tail.
 *
 * The socket does not replay a minute it never delivered. Callers re-seed
 * REST history on this, then keep applying live prints.
 */
export function liveMinuteSkips(
  bars: readonly CandleBar[],
  print: CandleBar,
  interval: ChartInterval,
  session: SessionFilter,
): boolean {
  if (!Number.isFinite(print.time)) return false;
  if (interval.unit === 'week' || interval.unit === 'month') return false;
  if (!inSession(print.time, interval, session)) return false;
  const last = bars[bars.length - 1];
  if (!last) return false;
  const widthMs = bucketMinutesOf(interval) * 60_000;
  const bucket = sessionBucket(print.time, bucketMinutesOf(interval));
  return bucket > last.time + widthMs;
}

/**
 * Fold a batch of live minutes onto chart bars, in time order.
 *
 * `skipped` is set when any print jumps more than one bar past the tail
 * that was on the chart when that print was applied.
 */
export function mergeLiveMinutes(
  bars: readonly CandleBar[],
  prints: readonly CandleBar[],
  interval: ChartInterval,
  session: SessionFilter,
): { bars: readonly CandleBar[]; skipped: boolean } {
  const ordered = [...prints].sort((a, b) => a.time - b.time);
  let next = bars;
  let skipped = false;
  for (const print of ordered) {
    if (!skipped && liveMinuteSkips(next, print, interval, session)) skipped = true;
    next = mergeLiveMinute(next, print, interval, session);
  }
  return { bars: next, skipped };
}

function adoptTail(
  server: readonly CandleBar[],
  current: readonly CandleBar[],
  interval: ChartInterval,
  session: SessionFilter,
): readonly CandleBar[] {
  const lastServer = server[server.length - 1].time;
  let next: readonly CandleBar[] = server;
  let kept = false;
  for (const bar of current) {
    if (bar.time <= lastServer) continue;
    const merged = mergeLiveMinute(next, bar, interval, session);
    if (merged === next) continue;
    next = merged;
    kept = true;
  }
  return kept ? next : server;
}

/**
 * Take server history as the base and keep live bars the snapshot does not list.
 *
 * The server owns a timestamp it returned, including the forming bar. On a
 * one-minute chart, a minute already drawn stays when the snapshot omits it,
 * even if it is older than the server's last bar. Removing that timestamp
 * leaves a blank candle wherever a flat study still holds the time. Wider
 * intervals keep only the tail past the server's last bar. An empty snapshot
 * leaves the live series alone.
 */
export function adoptHistory(
  server: readonly CandleBar[],
  current: readonly CandleBar[],
  interval: ChartInterval,
  session: SessionFilter,
): readonly CandleBar[] {
  if (server.length === 0) return current.length > 0 ? current : server;
  if (!(interval.unit === 'minute' && interval.count === 1)) {
    return adoptTail(server, current, interval, session);
  }
  const owned = new Set(server.map((bar) => bar.time));
  const extras: CandleBar[] = [];
  for (const bar of current) {
    if (!inSession(bar.time, interval, session)) continue;
    if (owned.has(bar.time)) continue;
    extras.push(bar);
    owned.add(bar.time);
  }
  if (extras.length === 0) return server;
  return [...server, ...extras].sort((a, b) => a.time - b.time);
}
